import { useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme/tokens';
import { CumulativeSpendChart } from './CumulativeSpendChart';
import { MonthlyExpensesChart } from './MonthlyExpensesChart';
import type { CumulativeSeries, MonthlyExpense } from './dashboard.types';
import { Panel } from './ui';

type View = 'month' | 'months';

/** "Ritmo dos gastos": the month's run with its projection, or six months side by side. */
export function RhythmCard({ cumulative, months, currency }: { cumulative: CumulativeSeries; months: MonthlyExpense[]; currency: string }) {
  const { t } = useTranslation('dashboard');
  const [view, setView] = useState<View>('month');
  const title = t('home.rhythm.title');

  const option = (value: View, label: string) => {
    const active = view === value;
    return (
      <ButtonBase
        aria-pressed={active}
        onClick={() => setView(value)}
        sx={{
          minHeight: 34,
          px: 1.75,
          borderRadius: 99,
          font: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          color: active ? tokens.ink : tokens.ink3,
          bgcolor: active ? tokens.surface : 'transparent',
          boxShadow: active ? '0 1px 2px rgba(15,27,31,.12)' : 'none',
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
        }}
      >
        {label}
      </ButtonBase>
    );
  };

  return (
    <Panel label={title}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1.25 }}>
        <Box component="h2" sx={{ m: 0, fontFamily: tokens.display, fontSize: 17, fontWeight: 650 }}>{title}</Box>
        <Box role="group" aria-label={t('home.rhythm.period')} sx={{ display: 'inline-flex', gap: '2px', p: '3px', borderRadius: 99, bgcolor: tokens.bg }}>
          {option('month', cumulative.closed ? t('home.rhythm.thatMonth') : t('home.rhythm.thisMonth'))}
          {option('months', t('home.rhythm.sixMonths'))}
        </Box>
      </Box>
      {view === 'month' ? (
        <CumulativeSpendChart {...cumulative} currency={currency} />
      ) : (
        <MonthlyExpensesChart months={months} currency={currency} />
      )}
    </Panel>
  );
}
